import { z } from 'zod';
import { inSeconds } from './date.js';
export const tenantIdKey = 'tenant_id';
/**
 * The ACR vocabulary lives in `../types/authentication-context.js` as `LogtoAcr`, alongside the
 * proof-classification and AMR tables that decide which class an interaction reaches, and the
 * `RequestedAuthenticationContext` the OIDC interaction policy writes into the login prompt
 * details — step-up signaling does not go through an `ExtraParamsKey` at all.
 *
 * @see {@link https://www.rfc-editor.org/rfc/rfc9470 | RFC 9470 OAuth 2.0 Step Up Authentication Challenge Protocol}
 */
export const oidcRoutes = Object.freeze({
    codeVerification: '/oidc/device',
});
export const customClientMetadataDefault = Object.freeze({
    idTokenTtl: inSeconds.oneHour,
    refreshTokenTtlInDays: 14,
    rotateRefreshToken: true,
});
export var ExtraParamsKey;
(function (ExtraParamsKey) {
    /**
     * @deprecated Use {@link FirstScreen} instead.
     * @see {@link InteractionMode} for the available values.
     */
    ExtraParamsKey["InteractionMode"] = "interaction_mode";
    /**
     * The first screen to show for the user.
     *
     * @see {@link FirstScreen} for the available values.
     */
    ExtraParamsKey["FirstScreen"] = "first_screen";
    /**
     * Directly sign in via the specified method. Note that the method must be properly configured
     * in Logto.
     *
     * @remark
     * The format of the value for this key is one of the following:
     *
     * - `social:<target>` (Use a social connector with the specified target, e.g. `social:google`)
     * - `sso:<connector-id>` (Use the specified SSO connector, e.g. `sso:123456`)
     */
    ExtraParamsKey["DirectSignIn"] = "direct_sign_in";
    /**
     * Override the default sign-in experience configuration with the settings from the specified
     * organization ID.
     */
    ExtraParamsKey["OrganizationId"] = "organization_id";
    /**
     * Provides a hint about the login identifier the user might use.
     * This can be used to pre-fill the identifier field **only on the first screen** of the sign-in/sign-up flow.
     */
    ExtraParamsKey["LoginHint"] = "login_hint";
    /**
     * The end-users preferred languages to use for the client application, represented as a space-separated list of BCP47 language tags.
     * E.g. `en` or `en-US` or `en-US en`.
     *
     * @see {@link https://openid.net/specs/openid-connect-core-1_0.html#rfc.section.13.2.1}
     */
    ExtraParamsKey["UiLocales"] = "ui_locales";
    /**
     * Specifies the identifier used in the identifier sign-in or identifier register page.
     *
     * This parameter is applicable only when first_screen is set to either `FirstScreen.IdentifierSignIn` or `FirstScreen.IdentifierRegister`.
     * Multiple identifiers can be provided in the identifier parameter, separated by spaces.
     *
     * If the provided identifier is not supported in the Logto sign-in experience configuration, it will be ignored,
     * and if no one of them is supported, it will fallback to the sign-in / sign-up method value set in the sign-in experience configuration.
     *
     * @see {@link SignInIdentifier} for available values.
     */
    ExtraParamsKey["Identifier"] = "identifier";
    /**
     * The one-time token used as a proof for the user's identity. Example use case: Magic link.
     */
    ExtraParamsKey["OneTimeToken"] = "one_time_token";
    /**
     * The Google One Tap credential JWT token for external website integration.
     */
    ExtraParamsKey["GoogleOneTapCredential"] = "google_one_tap_credential";
    /**
     * Overrides the theme that the sign-in experience would otherwise resolve from the end-user's
     * OS settings, so an application with its own light / dark toggle can keep Logto in sync.
     *
     * The parameter is ignored when dark mode is disabled in the sign-in experience configuration,
     * and an unrecognized value is ignored without affecting the other parameters.
     *
     * The available values are `light` and `dark`.
     */
    ExtraParamsKey["Theme"] = "theme";
})(ExtraParamsKey || (ExtraParamsKey = {}));
/** @deprecated Use {@link FirstScreen} instead. */
export var InteractionMode;
(function (InteractionMode) {
    InteractionMode["SignIn"] = "signIn";
    InteractionMode["SignUp"] = "signUp";
})(InteractionMode || (InteractionMode = {}));
export var FirstScreen;
(function (FirstScreen) {
    FirstScreen["SignIn"] = "sign_in";
    FirstScreen["Register"] = "register";
    FirstScreen["ResetPassword"] = "reset_password";
    FirstScreen["IdentifierSignIn"] = "identifier:sign_in";
    FirstScreen["IdentifierRegister"] = "identifier:register";
    FirstScreen["SingleSignOn"] = "single_sign_on";
    /** @deprecated Use snake_case 'sign_in' instead. */
    FirstScreen["SignInDeprecated"] = "signIn";
})(FirstScreen || (FirstScreen = {}));
export const extraParamsObjectGuard = z
    .object({
    [ExtraParamsKey.InteractionMode]: z.nativeEnum(InteractionMode),
    [ExtraParamsKey.FirstScreen]: z.nativeEnum(FirstScreen),
    [ExtraParamsKey.DirectSignIn]: z.string(),
    [ExtraParamsKey.OrganizationId]: z.string(),
    [ExtraParamsKey.LoginHint]: z.string(),
    [ExtraParamsKey.UiLocales]: z.string(),
    [ExtraParamsKey.Identifier]: z.string(),
    [ExtraParamsKey.OneTimeToken]: z.string(),
    [ExtraParamsKey.GoogleOneTapCredential]: z.string(),
    [ExtraParamsKey.Theme]: z.string(),
})
    .partial();
